<template>
  <DynamicScrollerItem
    :active="active"
    :item="item"
    :size-dependencies="sizeDependencies"
  >
    <div
      class="sf-virtual-table-row__container"
      :data-testid="testId"
      :style="rowStyle"
    >
      <div
        v-if="withSelector"
        class="
          tw-flex
          tw-justify-end
          tw-border-0
          tw-border-b
          tw-border-solid
          tw-border-gray-100
        "
        :style="selectorStickyStyle"
      >
        <SfCheckbox
          v-model="writableSelect"
          :disabled="disableSelector"
        ></SfCheckbox>
      </div>
      <slot />
    </div>
  </DynamicScrollerItem>
</template>

<script setup>
import { computed, inject, toRef } from 'vue'
import { DynamicScrollerItem } from 'vue-virtual-scroller'
import SfCheckbox from '@app/shared/vue/components/sf-checkbox'
import { useSelectorSticky } from '@app/shared/vue/composables/use-selector-sticky'

const props = defineProps({
  active: {
    type: Boolean,
    default: false,
  },
  item: {
    type: Object,
    required: true,
  },
  sizeDependencies: {
    type: Array,
    default: () => [],
  },
  withSelector: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  disableSelector: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['selectRow'])

const gridTemplateColumns = inject('gridTemplateColumns', '')
const horizontallyScrollable = inject('horizontallyScrollable', false)
const minTotalWidth = inject('minTotalWidth', 0)

const testId = computed(() =>
  props.active ? 'sf-virtual-table-row' : 'sf-virtual-table-row-inactive'
)

const rowStyle = computed(() => {
  const style = {
    'grid-template-columns': gridTemplateColumns.value,
  }

  if (horizontallyScrollable.value) {
    style['min-width'] = `${minTotalWidth.value}px`
  }

  return style
})

const writableSelect = computed({
  get: () => props.selected,
  set: (newValue) => {
    emit('selectRow', newValue)
  },
})

// Sticky selector styling
const { selectorStickyStyle } = useSelectorSticky({
  withSelector: toRef(props, 'withSelector'),
})
</script>

<style lang="scss" scoped>
@import 'silverfin-ui-kit/src/scss/tooling';

.sf-virtual-table-row__container {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  padding-bottom: 2px;

  // Clip the row tint to the content box so it never paints the 2px gutter.
  background-clip: content-box;
}
</style>
